/**
 * Durable client-side chat outbox — IndexedDB-backed.
 *
 * Replaces the previous localStorage-based outbox in Chat.jsx (same
 * per-conversation key shape, same add/remove/load semantics) — the
 * approved chat redesign requires IndexedDB specifically because
 * localStorage is synchronous, string-blob-only (no per-row writes,
 * whole-blob rewrite on every change), and has a small shared-origin quota,
 * all of which make it a poor fit for "the durable record of pending sends"
 * under exactly the burst-send conditions this redesign targets.
 *
 * A row is only ever removed by the caller once persistence is genuinely
 * confirmed (message_saved, HTTP 201, or the reconciliation endpoint) —
 * this module has no opinion on when that is; it is pure storage.
 *
 * If IndexedDB itself is unavailable (private browsing restrictions, quota
 * errors, disabled storage), every method here resolves to a safe default
 * (empty array / no-op) rather than throwing — callers should treat that as
 * "outbox not durable this session" rather than crash. Chat.jsx's existing
 * in-memory failedQueue ref continues to work in that case; only the
 * survive-a-refresh guarantee is lost, silently and without duplicating a
 * message.
 */

const DB_NAME = 'glos_chat_outbox';
const DB_VERSION = 1;
const STORE_NAME = 'outbox';

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') { resolve(null); return; }
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // keyPath is a compound-ish string key: `${appId}:${convId}:${tempId}`
          // so all rows live in one object store, queryable per-conversation
          // via the `convKey` index below without needing a separate store
          // per conversation.
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('convKey', 'convKey', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        console.error('[chatOutbox] failed to open IndexedDB:', req.error?.message);
        resolve(null);
      };
    } catch (e) {
      console.error('[chatOutbox] indexedDB.open threw:', e.message);
      resolve(null);
    }
  });
  return _dbPromise;
}

function convKeyFor(appId, convId) { return `${appId}:${convId}`; }
function rowKeyFor(appId, convId, tempId) { return `${appId}:${convId}:${tempId}`; }

// Memoizes the migration so it only ever actually runs once per page load,
// regardless of how many callers (Chat.jsx's explicit call, loadOutbox's
// lazy call) trigger it concurrently — they all await the same promise.
let _migrationPromise = null;
function ensureMigrated() {
  if (!_migrationPromise) _migrationPromise = migrateLegacyLocalStorageOutbox();
  return _migrationPromise;
}

/**
 * Returns all pending outbox items for one conversation, oldest first
 * (insertion order). Resolves to [] if IndexedDB is unavailable — this is
 * the "outbox not durable" degraded case, not an error the caller needs to
 * branch on specially (per the approved design: expose it via the UI
 * warning added by the caller, not by throwing here).
 *
 * Waits for the legacy localStorage migration to finish first (see
 * migrateLegacyLocalStorageOutbox below) — this guarantees a caller can
 * never read the outbox mid-migration and miss just-migrated items on first
 * paint. The migration is started lazily on first call, so a caller that
 * never explicitly triggers it still gets this ordering guarantee for free.
 */
async function loadOutbox(appId, convId) {
  await ensureMigrated();
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const idx = store.index('convKey');
      const req = idx.getAll(convKeyFor(appId, convId));
      req.onsuccess = () => resolve((req.result || []).map((r) => r.item));
      req.onerror = () => { console.error('[chatOutbox] loadOutbox failed:', req.error?.message); resolve([]); };
    } catch (e) {
      console.error('[chatOutbox] loadOutbox threw:', e.message);
      resolve([]);
    }
  });
}

/**
 * Adds one pending item, keyed by (appId, convId, item._tempId). A second
 * add for the same tempId overwrites in place (same semantics as the old
 * localStorage version's "already present, skip" — here it's idempotent
 * put rather than an explicit skip, since IndexedDB put() is naturally
 * idempotent on the same key and callers never intend a second, distinct
 * pending item for one clientMessageId).
 *
 * Resolves silently (does not reject) when IndexedDB is unavailable in this
 * session (openDb() returned null) — that is the documented "not durable
 * this session" degraded case, surfaced separately via isOutboxDurable(),
 * not an error this call needs to report.
 *
 * REJECTS if IndexedDB IS available but the write itself fails (quota
 * exceeded, a genuine transaction error, etc.) — callers that need to know
 * whether the write actually succeeded (e.g. the migration below, which
 * must not delete localStorage data unless the IndexedDB write for it is
 * confirmed) must catch this. Chat.jsx's own call site treats a caught
 * failure the same as "not durable this session": the send still proceeds
 * (the in-memory failedQueue still works within the session), it just
 * cannot be guaranteed to survive a refresh.
 */
async function addToOutbox(appId, convId, item) {
  const db = await openDb();
  if (!db) return; // durability unavailable this session — caller's in-memory queue still works
  return new Promise((resolve, reject) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put({
        key: rowKeyFor(appId, convId, item._tempId),
        convKey: convKeyFor(appId, convId),
        item,
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        const msg = tx.error?.message || 'unknown IndexedDB write error';
        console.error('[chatOutbox] addToOutbox failed:', msg);
        reject(new Error(msg));
      };
    } catch (e) {
      console.error('[chatOutbox] addToOutbox threw:', e.message);
      reject(e);
    }
  });
}

/**
 * Removes one pending item by clientMessageId. Must only be called once
 * persistence is genuinely confirmed (message_saved / HTTP 201 /
 * reconciliation) — never on socket-ack alone. See Chat.jsx call sites.
 */
async function removeFromOutbox(appId, convId, tempId) {
  const db = await openDb();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(rowKeyFor(appId, convId, tempId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => { console.error('[chatOutbox] removeFromOutbox failed:', tx.error?.message); resolve(); };
    } catch (e) {
      console.error('[chatOutbox] removeFromOutbox threw:', e.message);
      resolve();
    }
  });
}

/** True if IndexedDB opened successfully this session (for a UI warning if not). */
async function isOutboxDurable() {
  const db = await openDb();
  return !!db;
}

// ─── One-time localStorage → IndexedDB outbox migration ─────────────────────
// The previous chat implementation stored pending (unconfirmed) sends in
// localStorage under keys `chat_outbox_{appId}_{convId}` (see git history —
// this shipped and is part of the deployed app, so real users can genuinely
// have real pending messages sitting there right now). The IndexedDB outbox
// above never reads that key, so without this migration those pending
// messages would silently become invisible to the restore-on-mount effect
// after this change deploys — a real, not hypothetical, message-loss risk.
//
// Safety rules (all required, per the approved migration design):
//   - Never delete the localStorage record until the IndexedDB write for
//     that exact item is confirmed complete.
//   - Reuse the original clientMessageId (_tempId) — never regenerate.
//   - Idempotent: safe to call on every app load. addToOutbox's put() is
//     itself idempotent on the same key, and a completion flag additionally
//     short-circuits re-scanning once done, so this never re-processes
//     already-migrated data.
//   - If a given item's IndexedDB write fails, its localStorage record is
//     left in place (not deleted) so it can be retried on the next load.
//   - Does not touch already-persisted messages — this only ever reads/
//     writes pending-send outbox keys, never chat_messages / any message
//     history the app has separately loaded from the server.
const MIGRATION_DONE_KEY = 'chat_outbox_idb_migration_done_v1';
const LEGACY_KEY_RE = /^chat_outbox_([^_]+)_([^_]+)$/;

async function migrateLegacyLocalStorageOutbox() {
  try {
    if (typeof localStorage === 'undefined') return { migrated: 0, skipped: true };
    if (localStorage.getItem(MIGRATION_DONE_KEY) === '1') return { migrated: 0, skipped: true };

    const db = await openDb();
    if (!db) {
      // IndexedDB unavailable this session — do NOT mark migration done and
      // do NOT touch localStorage. Leave the legacy data exactly as-is so a
      // future session (once IndexedDB is available) can still migrate it.
      return { migrated: 0, skipped: true, reason: 'indexeddb_unavailable' };
    }

    let migratedCount = 0;
    let anyFailure = false;
    const legacyKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && LEGACY_KEY_RE.test(k)) legacyKeys.push(k);
    }

    for (const key of legacyKeys) {
      const match = key.match(LEGACY_KEY_RE);
      const [, appId, convId] = match;
      let items;
      try {
        items = JSON.parse(localStorage.getItem(key) || '[]');
      } catch (e) {
        console.error(`[chatOutbox][migrate] failed to parse legacy key ${key}:`, e.message);
        anyFailure = true;
        continue; // leave this key in place — don't lose data on a parse error
      }
      if (!Array.isArray(items) || items.length === 0) {
        // Nothing pending under this key — safe to remove immediately, no
        // IndexedDB write needed since there's nothing to preserve.
        localStorage.removeItem(key);
        continue;
      }

      let allOk = true;
      for (const item of items) {
        if (!item || !item._tempId) { allOk = false; continue; }
        try {
          // Reuses the exact same clientMessageId (_tempId) already on the
          // item — never generates a new one. put() is idempotent, so a
          // retry of a partially-completed migration for this key is safe.
          await addToOutbox(appId, convId, item);
        } catch (e) {
          console.error(`[chatOutbox][migrate] IndexedDB write failed for tempId=${item._tempId}:`, e.message);
          allOk = false;
        }
      }

      if (allOk) {
        // Only remove the legacy record once every item in it is confirmed
        // written to IndexedDB — per the safety rule, never delete before
        // the durable write succeeds.
        localStorage.removeItem(key);
        migratedCount += items.length;
      } else {
        anyFailure = true;
        // Leave the legacy key in place; next load will retry it (put() is
        // idempotent, so already-migrated items in this same key are safe
        // to write again).
      }
    }

    // Only mark migration fully done if nothing failed — otherwise leave it
    // unmarked so the next app load retries any keys that didn't fully
    // migrate (their data is still safely in localStorage).
    if (!anyFailure) {
      try { localStorage.setItem(MIGRATION_DONE_KEY, '1'); } catch { /* non-fatal */ }
    }

    return { migrated: migratedCount, skipped: false, hadFailure: anyFailure };
  } catch (e) {
    console.error('[chatOutbox][migrate] unexpected error, leaving localStorage untouched:', e.message);
    return { migrated: 0, skipped: true, reason: 'unexpected_error' };
  }
}

export { loadOutbox, addToOutbox, removeFromOutbox, isOutboxDurable, ensureMigrated as migrateLegacyLocalStorageOutbox };
